import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";
import dayjs from "dayjs";
import imageCompression from "browser-image-compression";

const bonusesCutsApi = factory.get("bonuses_cuts");

const initialState = {
  results: {
    loading: false,
    data: [],
    total: 0,
    sum: "",
    filters: {
      skip: 0,
      take: 25,
      page: 0,
      search: "",
      department_id: null,
      date: null,
      year: null,
      user_id: null,
      type: null,
      status_id: null,
      is_over_time: 0,
    },
    filters_dialog: false,
  },
  status: [
    { id: 1, ar_name: "في الانتظار", en_name: "new", color: "warning" },
    { id: 2, ar_name: "موافقة", en_name: "Accept", color: "success" },
    { id: 3, ar_name: "مرفوضة", en_name: "Reject", color: "error" },
  ],
  typeList: [
    { id: "bonus", label: "مكافأة" },
    { id: "deduction", label: "خصم" },
  ],
  bonuses_cuts: {
    loading: false,
    dialog: false,
    form: {
      id: "",
      user_id: null,
      amount: null,
      type: "bonus",
      date: new Date(),
      description: null,
      is_over_time: 0,
      attachments: null,
      prev_attachments: null,
      super_admin_form: {
        status_id: null,
        approved_amount: 0,
        note: "",
      },
    },
  },
  change_bonus_status_dialog: false,
};

export const bonusesCutsSlice = createSlice({
  name: "bonusesCuts",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setDialog: (state, action) => {
      state.bonuses_cuts.dialog = !state.bonuses_cuts.dialog;
    },
    setDataTable: (state, { payload }) => {
      state.results.data = payload.data.data;
      state.results.total = payload.data.total;
      state.results.sum = payload.data.sum;
    },
    setById: (state, { payload }) => {
      for (const key in state.bonuses_cuts.form) {
        if (payload[key]) {
          state.bonuses_cuts.form[key] = payload[key];
        }
      }
      state.bonuses_cuts.form.user_id = {
        id: payload.employee_id,
        full_name: payload.employee_name,
        department_name: payload.department,
      };
      state.bonuses_cuts.form.super_admin_form = {
        status_id: payload.status_id,
        approved_amount:
          payload.approved_amount !== "0.00"
            ? payload.approved_amount
            : payload.amount,
        note: payload.note,
      };
    },
    resetForm: (state, action) => {
      state.bonuses_cuts.form = initialState.bonuses_cuts.form;
    },
    setFiltersDialog: (state, action) => {
      state.results.filters_dialog = !state.results.filters_dialog;
    },
    setFilters: (state, { payload }) => {
      state.results.filters = {
        ...state.results.filters,
        ...payload,
      };
    },
    resetFilters: (state) => {
      state.results.filters = initialState.results.filters;
    },
    setChangeBonusStatusDialog: (state) => {
      state.change_bonus_status_dialog = !state.change_bonus_status_dialog;
    },
  },
});

export const {
  setLoading,
  setDialog,
  setDataTable,
  setById,
  resetForm,
  setFilters,
  setFiltersDialog,
  resetFilters,
  setChangeBonusStatusDialog,
} = bonusesCutsSlice.actions;

export default bonusesCutsSlice.reducer;

const toNumber = (value) => {
  return typeof value === "number" ? value : parseInt(value.replace(/,/g, ""));
};

const compressImage = async (file) => {
  if (!file || !file.type.startsWith("image/")) return file;

  try {
    if (file.size < 400 * 1024) return file;

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    const compressed = await imageCompression(file, options);

    return new File([compressed], file.name, {
      type: compressed.type,
    });
  } catch (err) {
    console.warn("❗ Compression failed for:", file.name, err);
    return file;
  }
};

//axios
const getAll = () => async (dispatch, getState) => {
  try {
    dispatch(setLoading("results"));
    const filters = getState().bonusesCuts.results.filters;
    const filters_ = {
      ...filters,
      user_id: filters.user_id?.id || null,
      department_id: filters.department_id?.id || null,
    };
    const res = await bonusesCutsApi.getAll(filters_);
    dispatch(setDataTable(res));
    dispatch(setLoading("results"));
  } catch (err) {
    dispatch(setLoading("results"));
    throw new Error(err);
  }
};
const deleteById = (id) => async (dispatch) => {
  try {
    await bonusesCutsApi.delete(id);
    dispatch(
      showNotification({
        message: "تم حذف  بنجاح",
        type: "success",
      })
    );
    dispatch(getAll());
  } catch (err) {
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      })
    );
    throw new Error(err);
  }
};
const getById = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("bonuses_cuts"));
    const res = await bonusesCutsApi.getById(id);
    dispatch(setById(res.data.data[0]));
    dispatch(setDialog());
    dispatch(setLoading("bonuses_cuts"));
  } catch (err) {
    dispatch(setLoading("bonuses_cuts"));
    throw new Error(err);
  }
};
const create = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("bonuses_cuts"));

    const fd = new FormData();

    const form = {
      ...data,
      user_id: data.user_id?.id,
      date: `${dayjs(data.date).format("YYYY-MM")}-01`,
      amount: toNumber(data.amount),
    };

    delete form.super_admin_form;

    Object.entries(form).forEach(([key, value]) => {
      if (key === "attachments") return;
      if (key === "prev_attachments") return;
      fd.append(key, value);
    });

    if (Array.isArray(form.attachments)) {
      const files = form.attachments.slice(0, 3);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file) continue;

        const finalFile = await compressImage(file);
        fd.append("attachments[]", finalFile);
      }
    }

    await bonusesCutsApi.create(fd);
    dispatch(setLoading("bonuses_cuts"));

    dispatch(
      showNotification({
        message: "تم الاضافة بنجاح",
        type: "success",
      })
    );

    dispatch(setDialog());
    dispatch(getAll());
  } catch (err) {
    dispatch(setLoading("bonuses_cuts"));
    dispatch(
      showNotification({
        message: err?.response?.data?.errors?.[0] || "حدث خطأ أثناء الإضافة",
        type: "error",
      })
    );
    throw err;
  }
};

const update = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("bonuses_cuts"));
    const form = {
      ...data,
      user_id: data.user_id?.id,
      date: `${dayjs(data.date).format("YYYY-MM")}-01`,
      amount: toNumber(data.amount),
    };
    delete form.super_admin_form;
    await bonusesCutsApi.update(form);
    dispatch(setDialog());
    dispatch(
      showNotification({
        message: "تم التعديل بنجاح",
        type: "success",
      })
    );
    dispatch(getAll());
    dispatch(setLoading("bonuses_cuts"));
  } catch (err) {
    dispatch(setLoading("bonuses_cuts"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      })
    );
    throw new Error(err);
  }
};

const changeStatusForm = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("bonuses_cuts"));
    let form = {};
    if (data.super_admin_form) {
      form = {
        id: data.id,
        ...data.super_admin_form,
        approved_amount: toNumber(data.super_admin_form.approved_amount),
      };
    } else {
      form = {
        ...data,
      };
    }
    await bonusesCutsApi.changeStatusWithInfo(form);
    if (data.super_admin_form) {
      dispatch(setDialog());
    }
    dispatch(
      showNotification({
        message: "تم التعديل بنجاح",
        type: "success",
      })
    );
    dispatch(getAll());
    dispatch(setLoading("bonuses_cuts"));
  } catch (err) {
    dispatch(setLoading("bonuses_cuts"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      })
    );
    throw new Error(err);
  }
};

const changeMultiBonusStatus = (note) => async (dispatch) => {
  try {
    dispatch(setLoading("bonuses_cuts"));
    await bonusesCutsApi.changeMultiBonusStatus({ note, is_over_time: 0 });
    dispatch(
      showNotification({
        message: "تم تغيير الحالة بنجاح",
        type: "success",
      })
    );
    dispatch(setChangeBonusStatusDialog());
    dispatch(setLoading("bonuses_cuts"));
    dispatch(getAll());
  } catch (err) {
    dispatch(setLoading("bonuses_cuts"));
    dispatch(
      showNotification({
        message: err.response.data.errors,
        type: "error",
      })
    );
    throw new Error(err);
  }
};

const toExcel = () => async (dispatch, getState) => {
  try {
    dispatch(setLoading("bonuses_cuts"));

    const filters = getState().bonusesCuts.results.filters;
    const custom_filters = {
      ...filters,
      user_id: filters.user_id?.id || null,
      department_id: filters.department_id?.id || null,
      is_excel: 1,
    };
    const res = await bonusesCutsApi.exportToExcel(custom_filters);
    let url = window.URL.createObjectURL(res.data);
    let a = document.createElement("a");
    a.href = url;
    a.download = "bonuses_cuts.xlsx";
    a.click();
    dispatch(setLoading("bonuses_cuts"));
  } catch (err) {
    dispatch(setLoading("bonuses_cuts"));
    throw new Error(err);
  }
};

export const BonusesCuts = {
  getAll,
  deleteById,
  getById,
  create,
  update,
  changeStatusForm,
  changeMultiBonusStatus,
  toExcel,
};
